import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, FormControlLabel, Switch, TextField } from '@mui/material';
import { BaseDialog } from '@/components/common/BaseDialog';
import { ModalIndices } from '@/common/i-doc/i-doc.registry.ts';
import { ConstructionOption, CreateOptionDto } from '@/types/questionnaire-construction';

interface OptionFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (dto: CreateOptionDto) => Promise<void>;
  editingOption: ConstructionOption | null;
  nextOrder: number;
}

function emptyForm(nextOrder: number): CreateOptionDto {
  return { value: '', order: nextOrder, isDefault: false, caption: '', nestCaptionI18n: { uk: '' } };
}

export const OptionFormDialog: React.FC<OptionFormDialogProps> = ({ open, onClose, onSave, editingOption, nextOrder }) => {
  const [form, setForm] = useState<CreateOptionDto>(emptyForm(nextOrder));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editingOption) {
      setForm({
        value: editingOption.value,
        order: editingOption.order,
        isDefault: !!editingOption.isDefault,
        caption: editingOption.caption ?? '',
        nestCaptionI18n: { uk: editingOption.nestCaptionI18n?.uk ?? editingOption.caption ?? '' },
      });
    } else {
      setForm(emptyForm(nextOrder));
    }
  }, [open, editingOption, nextOrder]);

  const captionValue = form.nestCaptionI18n?.uk ?? '';
  const isValid = form.value.trim().length > 0 && captionValue.trim().length > 0;

  const handleSave = async () => {
    if (!isValid) return;
    setSaving(true);
    try {
      await onSave({ ...form, caption: captionValue });
    } finally {
      setSaving(false);
    }
  };

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title={editingOption ? 'Редагувати варіант' : 'Новий варіант відповіді'}
      maxWidth="xs"
      iDoc={ModalIndices.QUIZ_BUILDER_OPTION_FORM}
      actions={
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!isValid || saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {saving ? 'Збереження...' : 'Зберегти'}
        </Button>
      }
    >
      <Box display="flex" flexDirection="column" gap={2}>
        <TextField
          label="Текст варіанту"
          value={captionValue}
          onChange={(e) => setForm(f => ({ ...f, nestCaptionI18n: { uk: e.target.value } }))}
          fullWidth
          size="small"
          autoFocus
        />
        <TextField
          label="Значення, що зберігається в БД"
          value={form.value}
          onChange={(e) => setForm(f => ({ ...f, value: e.target.value }))}
          fullWidth
          size="small"
          helperText="Латиницею, напр. option_a"
        />
        <FormControlLabel
          control={
            <Switch
              checked={!!form.isDefault}
              onChange={(e) => setForm(f => ({ ...f, isDefault: e.target.checked }))}
            />
          }
          label="Обрано за замовчуванням"
        />
      </Box>
    </BaseDialog>
  );
};
